import { gql } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import { Controller } from 'react-hook-form';
import { z } from 'zod';

import { Card } from '@repo/ui/Card';
import { Form } from '@repo/ui/Form';
import { Button } from '@repo/ui/Button';
import { useZodForm } from '@repo/hooks/useZodForm';
import { SwitchSelectorField } from '@/components/SwitchSelectorField';
import { useScrollToHash } from '@/hooks/useScrollToHash';

export const SWITCH_ANCHOR = 'switch';

const CHANGE_DID_SWITCH = gql`
  mutation ChangeDidSwitch($didId: ID!, $switchId: ID) {
    changeDidSwitch(didId: $didId, switchId: $switchId) {
      did {
        id
      }
    }
  }
`;

const schema = z.object({
  switch: z.object({ label: z.string(), value: z.string() }),
});

interface ChangeDidSwitchFormProps {
  didId: string;
  accountId: string;
  currentSwitch: { id: string; name: string } | null | undefined;
  refetch: () => void;
}

const ChangeDidSwitchForm = ({
  didId,
  accountId,
  currentSwitch,
  refetch,
}: ChangeDidSwitchFormProps) => {
  useScrollToHash(SWITCH_ANCHOR, true);

  return (
    <div id={SWITCH_ANCHOR} className="w-full scroll-mt-32">
      <Card
        title="Message Forwarding Switch"
        description="The switch decides where this number's incoming messages are forwarded. The account's oldest switch is its default."
      >
        <SwitchForm
          // Remount when the current switch changes so the form picks up the new default
          key={currentSwitch?.id ?? 'none'}
          didId={didId}
          accountId={accountId}
          currentSwitch={currentSwitch}
          onChanged={refetch}
        />
      </Card>
    </div>
  );
};

const SwitchForm = ({
  didId,
  accountId,
  currentSwitch,
  onChanged,
}: Omit<ChangeDidSwitchFormProps, 'refetch'> & { onChanged: () => void }) => {
  const client = useApolloClient();

  const form = useZodForm({
    schema,
    defaultValues: currentSwitch
      ? { switch: { label: currentSwitch.name, value: currentSwitch.id } }
      : undefined,
    onSubmit: async ({ switch: selected }) => {
      await client.mutate({
        mutation: CHANGE_DID_SWITCH,
        variables: { didId, switchId: selected.value },
      });
      onChanged();
    },
  });

  return (
    <Form {...form} column={1}>
      <Form.Success>Switch was successfully changed.</Form.Success>
      <Form.Error>Unable to change the switch, please try again.</Form.Error>

      {!currentSwitch && (
        <p className="text-sm sm:col-span-2">
          This number has no switch, so its messages are not forwarded.
        </p>
      )}

      <Controller
        control={form.control}
        name="switch"
        render={({ field: { onChange, value } }) => (
          <SwitchSelectorField
            accountId={accountId}
            value={value}
            onChange={onChange}
            error={form.formState.errors?.switch?.value}
            emptyMessage="This account has no switches yet."
            // Editing: an empty value means the number has no switch; don't fill it in
            preselectDefault={false}
          />
        )}
      />

      <div className="w-full sm:col-span-2">
        <Button type="submit">Save changes</Button>
      </div>
    </Form>
  );
};

export default ChangeDidSwitchForm;
